import { Box, Button, Chip, Container, Divider, Paper, Typography } from "@mui/material";
import { motion, Variants } from "framer-motion";
import GavelIcon from "@mui/icons-material/Gavel";
import GroupsIcon from "@mui/icons-material/Groups";
import { useState } from "react";
import { updateUser } from "../../../services/usersService";
import { useToastContext as useToast } from "../../../context/ToastContext";
import { useQueryClient } from "@tanstack/react-query";
import { TEAM_PAGE_CONTENT } from "../data/teamPageContent";
import { TeamKey } from "../../../models/TeamConstants";
import { User } from "../../../models/User";
import PageMeta from '../../../components/PageMeta';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.4, ease: "easeOut" as const },
  }),
};

interface TeamPageProps {
  teamKey: TeamKey;
  user: User | null;
  onUpdateUser: (user: User) => void;
  onShowLogin: () => void;
  meta: { title: string; description: string; canonical: string };
}

export default function TeamPage({ teamKey, user, onUpdateUser, onShowLogin, meta }: TeamPageProps) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const content = TEAM_PAGE_CONTENT[teamKey];

  if (!content) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography variant="h4">Team not found.</Typography>
      </Container>
    );
  }

  const handleJoin = async () => {
    if (loading) return;

    if (!user) {
      onShowLogin();
      return;
    }

    if (user.team === teamKey) {
      setLoading(true);
      addToast(content.alreadyOnTeamToast, { type: "info" });
      setTimeout(() => setLoading(false), 2000);
      return;
    }

    // Update team
    setLoading(true);
    try {
      const response = await updateUser({
        userId: user.userId,
        team: teamKey,
      });

      localStorage.setItem("triviaUser", JSON.stringify(response));
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onUpdateUser(response);
      addToast(content.joinSuccessToast, { type: "success" });
    } catch (err) {
      console.error("Failed to update team:", err);
      addToast("Couldn't update your team. Please try again.", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const isCurrentTeam = user?.team === teamKey;

  return (
    <>
    <PageMeta title={meta.title} description={meta.description} canonical={meta.canonical} />
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <Typography variant="h1" sx={{ fontSize: { xs: "3rem", md: "4rem" }, lineHeight: 1 }}>
            {content.emoji}
          </Typography>
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{ letterSpacing: "-0.5px" }}
          >
            {content.headline}
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 560, fontStyle: "italic" }}>
          {content.tagline}
        </Typography>

        {/* Join button */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleJoin}
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              bgcolor: content.color,
              color: "whitesmoke",
              "&:hover": { bgcolor: content.color, filter: "brightness(0.9)" },
            }}
          >
            {loading
                ? isCurrentTeam
                  ? `✓ You're a ${teamKey.slice(0, -1)}`  // already on team cooldown — don't say "Joining..."
                  : "Joining..."                            // actual API call in progress
                : isCurrentTeam
                ? `✓ You're a ${teamKey.slice(0, -1)}`
                : content.joinButtonLabel
            }
          </Button>

          {!user && (
            <Typography variant="body1" color="text.secondary">
              You'll need an account to join a team.
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />
      </motion.div>

      {/* Content sections */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4, mb: 5 }}>
        {content.sections.map((section, i) => (
          <motion.div
            key={section.title}
            custom={i}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <Box
                sx={{
                  mt: "2px",
                  flexShrink: 0,
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1,
                  bgcolor: content.color,
                  "& svg": { color: "white" },
                }}
              >
                <GroupsIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {section.title}
                </Typography>
                {i === content.sections.length - 1 ? (
                  <Box
                    component="ul"
                    sx={{ m: 0, pl: 2.5, color: "text.secondary" }}
                  >
                    {section.body
                      .split("\n")
                      .map((line) => line.trim())
                      .filter(Boolean)
                      .map((line) => (
                        <Typography
                          key={line}
                          component="li"
                          variant="body1"
                          color="text.secondary"
                          sx={{ lineHeight: 1.75, mb: 0.5 }}
                        >
                          {line}
                        </Typography>
                      ))}
                  </Box>
                ) : (
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                    {section.body}
                  </Typography>
                )}
              </Box>
            </Box>
          </motion.div>
        ))}
      </Box>

      {/* Other teams CTA */}
      <motion.div
        custom={content.sections.length}
        initial="hidden"
        animate="visible"
        variants={fadeUp}
      >
        <Paper
          variant="outlined"
          sx={{ p: 3, borderRadius: 2, bgcolor: "action.hover", borderColor: "divider", mb: 3 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <GroupsIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
              Not sure this is your team?
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 1.5 }}>
            You can explore all four teams and switch at any time from your profile page. Your stats
            stay with you regardless of which team you're on.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {(["Griffins", "Snakes", "Badgers", "Eagles"] as TeamKey[])
              .filter((t) => t !== teamKey)
              .map((t) => (
                <Chip
                  key={t}
                  label={`${TEAM_PAGE_CONTENT[t].emoji} ${t}`}
                  component="a"
                  href={`/teams/${t.toLowerCase()}`}
                  clickable
                  size="medium"
                  variant="outlined"
                />
              ))}
          </Box>
        </Paper>
      </motion.div>

      {/* Fan site disclaimer — matches About page */}
      <motion.div
        custom={content.sections.length + 1}
        initial="hidden"
        animate="visible"
        variants={fadeUp}
      >
        <Paper
          variant="outlined"
          sx={{ p: 3, borderRadius: 2, bgcolor: "action.hover", borderColor: "divider" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <GavelIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
              Fan Site Disclaimer
            </Typography>
            <Chip label="Not affiliated" size="small" variant="outlined" sx={{ ml: "auto" }} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            The Quizzler is an independent, unofficial fan site. Team names and descriptions are
            original creations and are{" "}
            <strong>not affiliated with, endorsed by, or connected to</strong> J.K. Rowling,
            Warner Bros. Entertainment, Wizarding World Digital, or any of their subsidiaries.
          </Typography>
        </Paper>
      </motion.div>
    </Container>
    </>
  );
}